import React, { useState, useRef, useEffect } from 'react';
import { Box, Modal, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { useApiCall } from '~/single-page/hooks/useApiCall';
import GalleryNGOClient from '~/single-page/api/GalleryNGOClient';
import NoItemsMessage from '~/components/NoItemsMessage';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';

export default function GalleryCards({ ong, action, type = 'gallery', autoplay = true }) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const swiperRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenModal = (index) => {
    setSelectedImageIndex(index);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const getImages = () => {
    if (action) {
      return type === 'gallery' ? action.gallery_images_url : action.accountability_images_url;
    } else if (ong) {
      return GalleryNGOClient.galleryNGO(ong.id);
    }
    return Promise.reject('Nem ONG nem Action fornecidos');
  };

  const {
    data: images = [],
    success,
    pending,
  } = useApiCall({
    apiCall: getImages,
    init: true,
  });

  const getMaxSlidesPerView = () => {
    return 3;
  };

  if (pending) {
    return (
      <Box sx={{ 
        '& .swiper-slide': {
          display: 'flex',
          justifyContent: 'center'
        },
        '& .swiper-pagination': {
          display: { xs: 'block', sm: 'none' }
        }
      }}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          navigation={true}
          pagination={isMobile ? { 
            clickable: true,
            renderBullet: function (index, className) {
              return '<span class="' + className + '" style="margin-top: 10px"></span>';
            }
          } : false}
          autoplay={autoplay ? {
            delay: 3000,
            disableOnInteraction: false,
          } : false}
          loop={false}
          slidesPerGroup={1}
          slidesPerView={1}
          spaceBetween={16}
          breakpoints={{
            600: {
              slidesPerView: 2,
              spaceBetween: 24,
            },
            900: {
              slidesPerView: 3,
              spaceBetween: 32,
            },
            1200: {
              slidesPerView: 3,
              spaceBetween: 32,
            }
          }}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <SwiperSlide key={index}>
              <Box
                component="div"
                sx={{
                  width: { xs: '100%', sm: 250 },
                  height: 160,
                  bgcolor: 'grey.200',
                  borderRadius: 1
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    );
  }

  if (!success || !images || images.length === 0) {
    return <NoItemsMessage itemName="imagem" gender="feminino" />;
  }

  const totalImages = images.length;
  const maxSlidesPerView = getMaxSlidesPerView();
  
  const getSafeSlideConfig = (defaultValue, total) => {
    return Math.min(defaultValue, total);
  };

  return (
    <Box sx={{ 
      position: 'relative',
      '& .swiper-slide': {
        display: 'flex',
        justifyContent: 'center'
      },
      '& .swiper-pagination': {
        display: { xs: 'block', sm: 'none' },
        position: 'static',
        marginTop: '15px'
      },
      '& .swiper-button-next, & .swiper-button-prev': {
        color: 'primary.main',
        width: '40px',
        height: '40px',
        transform: { xs: 'translateY(-15px)', sm: 'translateY(3px)' },
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '50%',
        '&::after': {
          fontSize: '20px',
          fontWeight: 'bold',
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
        },
      },
      '& .swiper-button-next': {
        right: '3px'
      },
      '& .swiper-button-prev': {
        left: '3px'
      }
    }}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        navigation={true}
        pagination={isMobile ? { 
          clickable: true,
          renderBullet: function (index, className) {
            return '<span class="' + className + '" style="margin-top: 10px"></span>';
          }
        } : false}
        autoplay={autoplay ? {
          delay: 3000,
          disableOnInteraction: false,
        } : false}
        loop={totalImages > 1}
        slidesPerGroup={1}
        spaceBetween={24}
        slidesPerView={1}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        breakpoints={{
          480: {
            slidesPerView: getSafeSlideConfig(1, totalImages),
            spaceBetween: 24,
          },
          768: {
            slidesPerView: getSafeSlideConfig(2, totalImages),
            spaceBetween: 32,
          },
          1024: {
            slidesPerView: getSafeSlideConfig(2, totalImages),
            spaceBetween: 40,
          },
          1200: {
            slidesPerView: getSafeSlideConfig(3, totalImages),
            spaceBetween: 40,
          }
        }}
      >
        {(images || []).map((imageUrl, index) => (
          <SwiperSlide key={index}>
            <Box
              component="img"
              src={imageUrl}
              alt={`Imagem ${index + 1}`}
              onClick={() => handleOpenModal(index)}
              sx={{
                width: { xs: '100%', sm: 250 },
                height: 160,
                objectFit: 'cover',
                borderRadius: 1,
                cursor: 'pointer',
                margin: '12px 0'
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-imagem"
        aria-describedby="modal-exibição-imagem"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: '90vw' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: { xs: 1, sm: 2 },
          outline: 'none',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 20,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              width: { xs: '32px', sm: '36px' },
              height: { xs: '32px', sm: '36px' },
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>

          {images.length > 1 && (
            <IconButton 
              onClick={handlePrevImage}
              sx={{ 
                position: 'absolute', 
                left: { xs: 2, sm: 8 }, 
                zIndex: 10,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                width: { xs: '40px', sm: '48px' },
                height: { xs: '40px', sm: '48px' },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
            >
              <ArrowBackIosNewIcon fontSize="medium" />
            </IconButton>
          )}

          <Box
            component="img"
            src={images[selectedImageIndex]}
            alt={`Imagem ${selectedImageIndex + 1}`}
            sx={{
              width: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
            }}
          />

          {images.length > 1 && (
            <IconButton 
              onClick={handleNextImage}
              sx={{ 
                position: 'absolute', 
                right: { xs: 2, sm: 8 }, 
                zIndex: 10,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                width: { xs: '40px', sm: '48px' },
                height: { xs: '40px', sm: '48px' },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
            >
              <ArrowForwardIosIcon fontSize="medium" />
            </IconButton>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
